import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { noop } from 'lodash-es';
import { useStateInRef, useTransaction } from 'seagull';
import { useIoContext, useStore, useStoreStatic } from '~/contexts';
import { FeatureFlag } from '~/featureFlags';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useRootDevice } from '~/providers/DeviceProvider';
import { selectors } from '~/store/state-selectors';
import { secondsUntilJwtExpiry, STALE_JWT_THRESHOLD_SECONDS } from './intercom-jwt-staleness';
const IntercomStarterContext = /*#__PURE__*/ createContext({
    setEnableIntercom: noop
});
export const useIntercomStarter = ()=>useContext(IntercomStarterContext);
export function IntercomStarter(param) {
    let { children } = param;
    const userInfo = useStore((state)=>state.userInfo);
    const { getState } = useStoreStatic();
    const isRealUser = useStore(selectors.isRealUser);
    const { intercomClient } = useIoContext();
    const rootDevice = useRootDevice();
    const getRootDevice = useStateInRef(rootDevice);
    const [enableIntercom, setEnableIntercom] = useState(false);
    const value = useMemo(()=>({
            setEnableIntercom
        }), []);
    const userEmail = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.email()) || '';
    const userId = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()) || '';
    const startTransaction = useTransaction();
    const featureFlags = useFeatureFlags();
    const featureFlagsLoaded = useStore((s)=>selectors.ff.featureFlags(s).data != null);
    const useJwtIdentity = featureFlags[FeatureFlag.INTERCOM_JWT_IDENTITY] === true;
    const businessId = useStore(selectors.user.businessId) || '';
    const lastModeRef = useRef('none');
    useEffect(()=>{
        if (!isRealUser || !enableIntercom) {
            if (lastModeRef.current !== 'shutdown') {
                intercomClient.shutdown();
                lastModeRef.current = 'shutdown';
            }
            return;
        }
        if (!featureFlagsLoaded) return;
        const hideDefaultLauncher = getRootDevice().isMobileDevice;
        const mode = useJwtIdentity ? 'jwt' : 'legacy';
        if (mode === 'legacy' && lastModeRef.current === 'legacy') return;
        if (lastModeRef.current === 'jwt' || lastModeRef.current === 'legacy') {
            intercomClient.shutdown();
        }
        if (mode === 'jwt') {
            const intercomUserJwt = selectors.intercomUserJwt(getState());
            const stalenessSeconds = secondsUntilJwtExpiry(intercomUserJwt);
            if (intercomUserJwt == null) {
                Sentry.captureMessage('Intercom boot without intercom_user_jwt', {
                    level: 'warning',
                    tags: {
                        context: 'intercom_missing_user_jwt'
                    },
                    extra: {
                        userId,
                        userEmail
                    }
                });
            } else if (stalenessSeconds != null && stalenessSeconds < STALE_JWT_THRESHOLD_SECONDS) {
                Sentry.captureMessage('Intercom JWT near or past expiry at boot', {
                    level: 'warning',
                    tags: {
                        context: 'intercom_jwt_stale'
                    },
                    extra: {
                        userId,
                        userEmail,
                        secondsUntilExpiry: stalenessSeconds
                    }
                });
            }
            intercomClient.boot({
                userId,
                email: userEmail,
                jwt: intercomUserJwt !== null && intercomUserJwt !== void 0 ? intercomUserJwt : undefined,
                hideDefaultLauncher
            });
        } else {
            const userHash = selectors.intercomUserHash(getState());
            if (!userHash) {
                Sentry.captureMessage('Intercom boot without user_hash', {
                    level: 'warning',
                    tags: {
                        context: 'intercom_missing_user_hash'
                    },
                    extra: {
                        userId,
                        userEmail
                    }
                });
            }
            intercomClient.boot({
                userId,
                email: userEmail,
                userHash: userHash || undefined,
                hideDefaultLauncher
            });
        }
        lastModeRef.current = mode;
    }, [
        userEmail,
        userId,
        businessId,
        isRealUser,
        enableIntercom,
        useJwtIdentity,
        featureFlagsLoaded,
        startTransaction
    ]);
    return /*#__PURE__*/ _jsx(IntercomStarterContext.Provider, {
        value: value,
        children: children
    });
}
